import { useMemo } from "react";
import Masonry from "react-responsive-masonry";
import { Route } from "react-router";
import { Routes } from "react-router-dom";
import { Icon } from "../../../../components/Icon/Icon.tsx";
import { PreserveSearchNavigate } from "../../../../components/Navigate/PreserveSearchNavigate.tsx";
import { Skeleton } from "../../../../components/Skeleton/Skeleton.tsx";
import { useDebouncedLoading } from "../../../../hooks/useDebouncedLoading.ts";
import { useOnMount } from "../../../../hooks/useOnMount.ts";
import {
  BRIEF_TOOL,
  GENERATE_TOOL,
  TRANSFER_TOOL,
} from "../../../../routes.ts";
import { selectedAssetUuidStore } from "../../../boardState/stores/selectedAssetUuidStore.ts";
import { AssetTile } from "../../components/AssetTile/AssetTile.tsx";
import { FAVORITE_TAG_NAME } from "../../constants.ts";
import { useBoard } from "../../hooks/useBoard.ts";
import { useBoardAssets } from "../../hooks/useBoardAssets.ts";
import { showDeletedAssetsStore } from "../../stores/showDeletedAssetsStore.ts";
import { tagsFilterStore } from "../../stores/tagsFilterStore.ts";
import { BulkActionsStripe } from "../components/BulkActionsStripe.tsx";
import { EmptyStateInformation } from "../components/EmptyStateInformation.tsx";
import { useDefaultCreateTool } from "../hooks/useDefaultTool.ts";
import { BaseTagFilterButton, TagsFilterBar } from "./TagsFilterBar.tsx";
import { filterAssetsByTags } from "./utils/tagFiltering.ts";

export const CreateWorkspace = () => {
  const defaultCreateTool = useDefaultCreateTool();
  return (
    <Routes>
      <Route index element={<CreateWorkspaceImageGrid />} />
      <Route path={GENERATE_TOOL} element={<CreateWorkspaceImageGrid />} />
      <Route path={TRANSFER_TOOL} element={<CreateWorkspaceImageGrid />} />
      <Route path={BRIEF_TOOL} element={<CreateWorkspaceImageGrid />} />
      <Route
        path="*"
        element={<PreserveSearchNavigate replace to={defaultCreateTool} />}
      />
    </Routes>
  );
};

const CreateWorkspaceImageGrid = () => {
  const { board } = useBoard();
  const { showDeletedAssets } = showDeletedAssetsStore.useState();
  const { assets, isLoading } = useBoardAssets({
    showDeletedAssets,
  });
  // XXX: We need to debounce the loading state to avoid flickering when entering board with no assets
  const debouncedIsLoading = useDebouncedLoading({ isLoading, delay: 100 });
  const { selectedTagsFilter } = tagsFilterStore.useState();

  useOnMount(() => {
    const selectedAssetUuid =
      selectedAssetUuidStore.getState().selectedAssetUuid;
    if (selectedAssetUuid) {
      document
        .getElementById(`AssetImageTile-${selectedAssetUuid}`)
        ?.scrollIntoView({ block: "center", behavior: "instant" });
      selectedAssetUuidStore.clearSelectedAssetUuid();
    }
  });

  const filteredAssets = useMemo(
    () =>
      assets
        ? showDeletedAssets
          ? assets.filter((asset) => asset.deleted_at !== null)
          : filterAssetsByTags({
              assets,
              filterTags: selectedTagsFilter,
            })
        : [],
    [assets, selectedTagsFilter, showDeletedAssets],
  );

  return (
    <div className="h-full flex-col gap-100 pt-400 transparent-scroll-container">
      <div className="px-600 flex-row gap-200">
        <TagsFilterBar tagsFilter={board.tags} />
        <ShowDeletedAssetsButton />
      </div>
      {(assets ?? isLoading) && (
        <div
          id="CreateWorkspaceGrid"
          className="flex-fill flex-col py-400 px-600 overflow-auto"
        >
          {filteredAssets.length > 0 || isLoading ? (
            <Masonry columnsCount={4} gutter="6px">
              {debouncedIsLoading
                ? Array.from({ length: 16 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      className="w-full aspect-square rounded-100"
                    />
                  ))
                : filteredAssets.map((asset) => (
                    <AssetTile key={asset.uuid} asset={asset} />
                  ))}
            </Masonry>
          ) : assets && assets.length === 0 ? (
            <EmptyStateInformation
              title="Nothing here yet!"
              body="Use the generation bar to start creating."
            />
          ) : selectedTagsFilter.length === 1 &&
            selectedTagsFilter[0].name === FAVORITE_TAG_NAME ? (
            <EmptyStateInformation
              title="No favorites yet!"
              body="Click on the heart on any image to save them here."
            />
          ) : showDeletedAssets ? (
            <EmptyStateInformation
              title="No archived images yet!"
              body="Archive images to see them here."
            />
          ) : (
            <EmptyStateInformation
              title="Nothing here yet!"
              body="Tag images to see them here."
            />
          )}
        </div>
      )}
      <BulkActionsStripe />
    </div>
  );
};

const ShowDeletedAssetsButton = () => {
  const { showDeletedAssets } = showDeletedAssetsStore.useState();

  return (
    <BaseTagFilterButton
      name="Archived"
      isSelected={showDeletedAssets}
      onClick={() => {
        showDeletedAssetsStore.toggleShowDeletedAssets();
        tagsFilterStore.resetTagsFilter();
      }}
      icon={<Icon name="Archive" size="sm" />}
    />
  );
};
